import './styles.scss';

import React, { useContext, useEffect, useState } from 'react';
import { useCountUp } from 'react-countup';
import ReactSpeedometer from 'react-d3-speedometer';

import { CoreContext } from '../../providers';
import { addUserData } from '../../state';
import { KaiBubbleBackground } from '../KaiBubbleBackground';

interface KaiScoreProps {
    countUpInitialValue: number;
    countUpMaximumValue: number;
    /**
     * Duration (in seconds) of one count up iteration - from `initial`
     * to `maximum` value.
     * @default 3
     */
    countDuration?: number;
    /**
     * Infinitely counts up from `initial` to `maximum` value then reverts the
     * counter from `maximum` to `initial`
     *
     * @default false
     */
    countUpLoopMode?: boolean;
    /**
     * This text will be shown once counter finishes its count up.
     * It won't be displayed if `countUpLoopMode` is set to true.
     */
    countUpEndText?: string;

    variant?: 'bubble' | 'speedometer';
}

export const KaiScore: React.FC<KaiScoreProps> = ({
    countUpInitialValue,
    countDuration = 3,
    countUpMaximumValue,
    countUpLoopMode = false,
    countUpEndText = '',
    variant = 'bubble',
    children,
}) => {
    /**
     * The `initial` and `loop` states are used exclusively in `countUpLoopMode`.
     * and helps to distinguish if we should rerun the counter
     * (see the useEffect below).
     *
     * The `finished` state is used to diplay `countUpEndText` when count up
     * finished and loop mode is disabled.
     */
    const [countUpIterationStatus, setCountUpIterationStatus] = useState<
        'initial' | 'finished' | 'loop'
    >('initial');

    const { countUp, update } = useCountUp({
        start: countUpInitialValue,
        end: countUpMaximumValue,
        duration: countDuration,
        onEnd: () =>
            setCountUpIterationStatus(countUpLoopMode ? 'loop' : 'finished'),
    });

    /**
     * `countUp` returns `string | number` so we need to parse its
     * value before we pass it to KaiScore
     */
    const parsedCountUp = parseInt(countUp.toString(), 10);

    /**
     * TODO: Is this needed? Remove?
     */
    const { store } = useContext(CoreContext);
    useEffect(() => {
        if (parsedCountUp === countUpMaximumValue) {
            store.dispatch(addUserData('mainCounter', true));
        }
    }, []);

    /**
     * This effect allows counter to count up to value that we are updating
     * once it reaches the end - means that with enabled `countUpLoopMode` we
     * will infinitely run the counter back and forth.
     *
     * For example: countUpInitialValue: 75, countUpMaximumValue: 100
     * First iteration: 75 to 100
     * Second iteration: 100 to 75
     */
    useEffect(() => {
        if (countUpLoopMode && countUpIterationStatus === 'loop') {
            update(
                parsedCountUp === countUpMaximumValue
                    ? countUpInitialValue
                    : countUpMaximumValue,
            );
            setCountUpIterationStatus('initial');
        }
    }, [countUpIterationStatus]);

    if (!parsedCountUp) {
        return null;
    }

    return variant === 'bubble' ? (
        <section className="KaiScore">
            <KaiBubbleBackground
                className="KaiScore__background-wrapper"
                score={parsedCountUp}
                /**
                 * TODO: Remove this `hacky` solution and create separate
                 * KaiScore component exclusively for error handling. This will
                 * allows us to remove the KaiScore with initial and max value `-1`
                 */
                scoreLimit={parsedCountUp === -1 ? 1000 : countUpMaximumValue}
            />
            <div className="KaiScore__content">
                {parsedCountUp === -1 ? (
                    <span className="KaiScore__received-score">Ups...</span>
                ) : (
                    <>
                        <span className="KaiScore__received-score">
                            {countUpIterationStatus !== 'finished' &&
                                parsedCountUp}
                            {countUpIterationStatus === 'finished' &&
                                countUpEndText}
                        </span>
                        <span className="KaiScore__limit-score">{`av ${countUpMaximumValue}`}</span>
                    </>
                )}
                {children}
            </div>
        </section>
    ) : (
        <div className="KaiScore__speedometer" style={{}}>
            <ReactSpeedometer
                width={500}
                fluidWidth={true}
                maxValue={countUpMaximumValue}
                value={parsedCountUp}
                segments={4000}
                maxSegmentLabels={0}
                needleColor="#133143"
                paddingVertical={25}
                valueTextFontSize={'22px'}
                currentValueText="Pengestyrke: ${value} av 1000"
                needleTransitionDuration={countDuration}
            />
        </div>
    );
};
